import { useEffect, useMemo, useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  type ImageSourcePropType,
} from "react-native";
import { ChevronRight, LockKeyhole, FlaskConical } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { CommonActions, useNavigation } from "@react-navigation/native";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { calculateBasicInfoCompletion } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";
import { useFertilityIntent } from "@src/packages/fertility-intent";
import { useCycles } from "@src/packages/cycles/hooks/useCycles";
import { getBlocksByKind, type ContentBlock } from "@src/packages/content";
import { getTestingRecommendations } from "./testingRecommendations";
import { Hero } from "@src/packages/homepage/plan/components/hero/Hero";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  MoonFilterChip,
  MoonSearchInput,
  SectionTitle,
  BackLink,
  EmptyState,
  cardSurface,
  PANEL_ON_DARK_PRESSED_FILL,
} from "@louise/moon-ui";
import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import {
  SCREEN_PADDING_TOP,
  semanticColors,
} from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { getTestRouteTarget } from "./testRouteIds";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import { TimelineSubtaskCard } from "@src/packages/homepage/plan/components/TimelineTaskComponents";
import AccessGridCard from "../../components/access/AccessGridCard";
import { getTestIllustration } from "../../components/access/testIllustrations";
import geneticScreeningIllustration from "../../../assets/images/testing-sections/genetic-screening-category.png";
import ovarianReserveIllustration from "../../../assets/images/testing-sections/ovarian-reserve-category.png";
import semenAnalysisIllustration from "../../../assets/images/testing-sections/semen-analysis-category.png";
import cycleMonitoringIllustration from "../../../assets/images/testing-sections/cycle-monitoring-category.png";
import implantationIllustration from "../../../assets/images/testing-sections/implantation-category.png";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { colors } from "@louise/theme/tokens";

/* ─── Section definitions ──────────────────────────────────────────── */

type SectionDef = {
  key: string;
  titleKey: string;
  descriptionKey: string;
  illustration: ImageSourcePropType;
};

type SectionGenderRelevance = "AFAB" | "AMAB" | "both";
type TestingFilterKey =
  | "all"
  | "egg-health"
  | "sperm-health"
  | "cycle-monitoring"
  | "embryo-genetics"
  | "uterus-implantation";

type TestingViewMode = "journey" | "topic";
type TimelineStageKey =
  | "check-fertility"
  | "during-treatment"
  | "embryos-and-transfer"
  | "after-transfer";

const TIMELINE_STAGES: {
  key: TimelineStageKey;
  titleKey: string;
  descriptionKey: string;
  illustration: ImageSourcePropType;
}[] = [
  {
    key: "check-fertility",
    titleKey: "access.testing.journey.check_fertility_title",
    descriptionKey: "access.testing.journey.check_fertility_desc",
    illustration: ovarianReserveIllustration,
  },
  {
    key: "during-treatment",
    titleKey: "access.testing.journey.during_treatment_title",
    descriptionKey: "access.testing.journey.during_treatment_desc",
    illustration: cycleMonitoringIllustration,
  },
  {
    key: "embryos-and-transfer",
    titleKey: "access.testing.journey.embryos_transfer_title",
    descriptionKey: "access.testing.journey.embryos_transfer_desc",
    illustration: geneticScreeningIllustration,
  },
  {
    key: "after-transfer",
    titleKey: "access.testing.journey.after_transfer_title",
    descriptionKey: "access.testing.journey.after_transfer_desc",
    illustration: implantationIllustration,
  },
];

const SECTIONS: (SectionDef & { gender: SectionGenderRelevance })[] = [
  {
    key: "ovarian-reserve",
    titleKey: "access.testing.sections.ovarian_reserve_title",
    descriptionKey: "access.testing.sections.ovarian_reserve_desc",
    illustration: ovarianReserveIllustration,
    gender: "AFAB",
  },
  {
    key: "semen-analysis",
    titleKey: "access.testing.sections.semen_analysis_title",
    descriptionKey: "access.testing.sections.semen_analysis_desc",
    illustration: semenAnalysisIllustration,
    gender: "AMAB",
  },
  {
    key: "cycle-monitoring",
    titleKey: "access.testing.sections.cycle_monitoring_title",
    descriptionKey: "access.testing.sections.cycle_monitoring_desc",
    illustration: cycleMonitoringIllustration,
    gender: "AFAB",
  },
  {
    key: "genetic-screening",
    titleKey: "access.testing.sections.genetic_screening_title",
    descriptionKey: "access.testing.sections.genetic_screening_desc",
    illustration: geneticScreeningIllustration,
    gender: "both",
  },
  {
    key: "implantation",
    titleKey: "access.testing.sections.implantation_title",
    descriptionKey: "access.testing.sections.implantation_desc",
    illustration: implantationIllustration,
    gender: "AFAB",
  },
];

const TESTING_FILTERS: {
  key: TestingFilterKey;
  labelKey: string;
  sectionKeys: string[];
}[] = [
  {
    key: "all",
    labelKey: "access.testing.filters.all_tests",
    sectionKeys: [],
  },
  {
    key: "egg-health",
    labelKey: "access.testing.filters.egg_health",
    sectionKeys: ["ovarian-reserve"],
  },
  {
    key: "sperm-health",
    labelKey: "access.testing.filters.sperm_health",
    sectionKeys: ["semen-analysis"],
  },
  {
    key: "cycle-monitoring",
    labelKey: "access.testing.filters.cycle_monitoring",
    sectionKeys: ["cycle-monitoring"],
  },
  {
    key: "embryo-genetics",
    labelKey: "access.testing.filters.embryo_genetics",
    sectionKeys: ["genetic-screening"],
  },
  {
    key: "uterus-implantation",
    labelKey: "access.testing.filters.uterus_implantation",
    sectionKeys: ["implantation"],
  },
];

/**
 * Order sections by relevance to the profile, but always show all of them.
 * Everyone can order any test (partners often order semen analysis), so nothing
 * is hidden. We just lead with what fits this person: an AFAB profile sees the
 * her-focused sections first and semen analysis lower down; an AMAB profile sees
 * semen analysis first. Unknown gender keeps the default order.
 */
function prioritizeSectionsForGender(gender: string) {
  if (gender !== "AFAB" && gender !== "AMAB") return SECTIONS;
  const relevant = SECTIONS.filter(
    (s) => s.gender === gender || s.gender === "both",
  );
  const rest = SECTIONS.filter(
    (s) => s.gender !== gender && s.gender !== "both",
  );
  return [...relevant, ...rest];
}

/* ─── Helpers ─────────────────────────────────────────────────────── */

const testingPlanHeroBg =
  require("../../../assets/images/care-hero-glow-gradient.png") as number;

const PGT_PARENT_ID = "test-preimplantation-genetic-testing";
const PGT_SUBTYPE_IDS = new Set([
  "test-pgt-a",
  "test-pgt-m",
  "test-pgt-sr",
  "test-pgt-p",
]);

const TIMELINE_VIEW_MODES: {
  key: TestingViewMode;
  labelKey: string;
}[] = [
  {
    key: "journey",
    labelKey: "access.testing.view.journey",
  },
  {
    key: "topic",
    labelKey: "access.testing.view.topic",
  },
];

function testHasTag(test: ContentBlock<"test">, tag: string) {
  return test.tags?.some((item) => String(item) === tag) ?? false;
}

function getTimelineStage(test: ContentBlock<"test">): TimelineStageKey {
  const testId = String(test.id);

  if (testHasTag(test, "post-cycle")) {
    return "after-transfer";
  }

  if (
    PGT_PARENT_ID === testId ||
    PGT_SUBTYPE_IDS.has(testId) ||
    test.body.section === "implantation"
  ) {
    return "embryos-and-transfer";
  }

  if (
    test.body.section === "cycle-monitoring" ||
    testHasTag(test, "during-cycle")
  ) {
    return "during-treatment";
  }

  return "check-fertility";
}

type TimelineGroupKey =
  | "for-you"
  | "for-partner"
  | "for-both"
  | "prepare-cycle"
  | "monitor-cycle"
  | "test-embryos"
  | "prepare-uterus"
  | "confirm-pregnancy";

function getTimelineGroupKey(
  stage: TimelineStageKey,
  test: ContentBlock<"test">,
): TimelineGroupKey {
  if (stage === "check-fertility") {
    if (testHasTag(test, "both-partners")) return "for-both";
    if (testHasTag(test, "his") && testHasTag(test, "her")) {
      return "for-both";
    }
    if (testHasTag(test, "his")) return "for-partner";
    if (testHasTag(test, "her")) return "for-you";
    return "for-both";
  }

  if (stage === "during-treatment") {
    return testHasTag(test, "during-cycle") ? "monitor-cycle" : "prepare-cycle";
  }

  if (stage === "embryos-and-transfer") {
    return test.body.section === "genetic-screening"
      ? "test-embryos"
      : "prepare-uterus";
  }

  return "confirm-pregnancy";
}

const TIMELINE_GROUP_TITLE_KEYS: Record<TimelineGroupKey, string> = {
  "for-you": "access.testing.journey.for_you",
  "for-partner": "access.testing.journey.for_partner",
  "for-both": "access.testing.journey.for_both",
  "prepare-cycle": "access.testing.journey.prepare_cycle",
  "monitor-cycle": "access.testing.journey.monitor_cycle",
  "test-embryos": "access.testing.journey.test_embryos",
  "prepare-uterus": "access.testing.journey.prepare_uterus",
  "confirm-pregnancy": "access.testing.journey.confirm_pregnancy",
};

const TIMELINE_GROUP_ORDER: Record<TimelineStageKey, TimelineGroupKey[]> = {
  "check-fertility": ["for-you", "for-partner", "for-both"],
  "during-treatment": ["prepare-cycle", "monitor-cycle"],
  "embryos-and-transfer": ["test-embryos", "prepare-uterus"],
  "after-transfer": ["confirm-pregnancy"],
};

function textMatchesQuery(
  values: (string | string[] | undefined)[],
  q: string,
) {
  return values.some((value) => {
    if (!value) return false;
    const text = Array.isArray(value) ? value.join(" ") : value;
    return text.toLowerCase().includes(q);
  });
}

function testMatchesSearch(test: ContentBlock<"test">, q: string) {
  const body = test.body;
  return textMatchesQuery(
    [
      test.title,
      test.subtitle,
      test.tags,
      body.testName,
      body.whatItMeasures,
      body.howItWorks,
      body.turnaroundTime,
      body.cycleDay,
    ],
    q,
  );
}

type RecommendedItem = {
  testId: string;
  title: string;
  reason: string;
};

function TestingRecommendationCards({
  items,
  onPress,
}: {
  items: RecommendedItem[];
  onPress: (testId: string) => void;
}) {
  const capturePress = usePressCapture("TestingRecommendationCards");
  if (items.length === 0) return null;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        gap: 10,
        paddingRight: 4,
      }}
      style={{
        marginTop: 16,
        marginHorizontal: -4,
      }}
    >
      {items.map((item) => {
        return (
          <Pressable
            key={item.testId}
            onPress={() => {
              capturePress(
                pressLabel(item.title),
                "testing_recommendation_card",
              );
              onPress(item.testId);
            }}
            accessibilityRole="button"
            accessibilityLabel={`${item.title}. ${item.reason}`}
            style={({ pressed }) => ({
              ...cardSurface({ variant: "panel", tone: "onDark" }),
              ...(pressed
                ? { backgroundColor: PANEL_ON_DARK_PRESSED_FILL }
                : null),
              width: 164,
              minHeight: 138,
              padding: 12,
              justifyContent: "space-between",
            })}
          >
            <View style={{ gap: 6, minWidth: 0 }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 6,
                }}
              >
                <Text
                  className="font-dm-sans"
                  numberOfLines={2}
                  style={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: "800",
                    color: semanticColors.onDark.text.primary,
                    lineHeight: 18,
                  }}
                >
                  {item.title}
                </Text>
                <ChevronRight
                  size={14}
                  color={semanticColors.onDark.text.secondary}
                  strokeWidth={1.8}
                />
              </View>

              <Text
                className="font-dm-sans"
                numberOfLines={3}
                style={{
                  fontSize: 11.5,
                  color: semanticColors.onDark.text.secondary,
                  lineHeight: 16,
                }}
              >
                {item.reason}
              </Text>
            </View>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

function TestingPlanHeroCard({
  items = [],
  onItemPress,
}: {
  items?: RecommendedItem[];
  onItemPress?: (testId: string) => void;
}) {
  const t = useTranslations();
  const navigation = useNavigation();
  const hasRecommendations = items.length > 0 && !!onItemPress;
  const recommendationContent =
    hasRecommendations && onItemPress ? (
      <TestingRecommendationCards items={items} onPress={onItemPress} />
    ) : undefined;

  return (
    <Hero
      style={{ marginTop: 16 }}
      backgroundSource={testingPlanHeroBg}
      eyebrow={{
        label: hasRecommendations
          ? t("access.testing.recommendations.eyebrow")
          : t("access.testing.hero.eyebrow"),
        icon: FlaskConical,
      }}
      title={
        hasRecommendations
          ? t("access.testing.recommendations.title")
          : t("access.testing.hero.title")
      }
      summary={
        hasRecommendations
          ? t("access.testing.recommendations.subtitle")
          : t("access.testing.hero.summary")
      }
      primaryAction={
        hasRecommendations
          ? undefined
          : {
              label: t("access.testing.hero.cta_unlock"),
              icon: LockKeyhole,
              onPress: () =>
                navigation.dispatch(
                  CommonActions.navigate({
                    name: "Care",
                    params: { screen: "MedicalHistory" },
                  }),
                ),
            }
      }
      bottomContent={recommendationContent}
    />
  );
}

function CanonicalTestGrid({
  tests,
  onPress,
}: {
  tests: ContentBlock<"test">[];
  onPress: (test: ContentBlock<"test">) => void;
}) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
      {tests.map((test) => (
        <View key={test.id} style={{ width: "48%", minHeight: 242 }}>
          <AccessGridCard
            title={test.title}
            description={test.subtitle ?? test.body.whatItMeasures}
            imageSource={getTestIllustration({
              testId: String(test.id),
              category: test.body.category,
            })}
            imageStyle={{ width: 74, height: 92 }}
            onPress={() => onPress(test)}
            action="testing_canonical_test_card"
            surface="panel"
          />
        </View>
      ))}
    </View>
  );
}

function TimelineTestGroups({
  stage,
  tests,
  onPress,
}: {
  stage: TimelineStageKey;
  tests: ContentBlock<"test">[];
  onPress: (test: ContentBlock<"test">) => void;
}) {
  const t = useTranslations();
  const hasPgtParent = tests.some((test) => String(test.id) === PGT_PARENT_ID);
  const visibleTests = tests.filter(
    (test) => !PGT_SUBTYPE_IDS.has(String(test.id)) || !hasPgtParent,
  );
  const groups = TIMELINE_GROUP_ORDER[stage]
    .map((groupKey) => ({
      groupKey,
      tests: visibleTests.filter(
        (test) => getTimelineGroupKey(stage, test) === groupKey,
      ),
    }))
    .filter(({ tests: groupTests }) => groupTests.length > 0);

  return (
    <View style={{ gap: 20 }}>
      {groups.map(({ groupKey, tests: groupTests }) => (
        <View key={groupKey} style={{ gap: 10 }}>
          <SectionTitle>{t(TIMELINE_GROUP_TITLE_KEYS[groupKey])}</SectionTitle>
          <CanonicalTestGrid tests={groupTests} onPress={onPress} />
        </View>
      ))}
    </View>
  );
}

/* ─── Component ───────────────────────────────────────────────────── */

type Props = NativeStackScreenProps<AccessStackParamList, "Testing">;

export default function TestingScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("TestingScreen");
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "AccessOverview",
  });
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<TestingViewMode>(() =>
    route.params?.focus ? "topic" : "journey",
  );
  const [activeFilter, setActiveFilter] = useState<TestingFilterKey>("all");
  const [expandedTimelineKey, setExpandedTimelineKey] =
    useState<TimelineStageKey | null>("check-fertility");
  const [expandedSectionKey, setExpandedSectionKey] = useState<string | null>(
    null,
  );

  const { data: patient } = usePatient();
  const { data: fertilityIntent } = useFertilityIntent();
  const { data: cycles } = useCycles();
  const authorizedExperience = useAuthorizedExperienceProjection();
  const canonicalExperience =
    authorizedExperience.status === "ready"
      ? authorizedExperience.canonical
      : null;
  const isProfileComplete = useMemo(
    () => calculateBasicInfoCompletion(patient).percent >= 0.999,
    [patient],
  );
  const recommendations = useMemo(
    () =>
      getTestingRecommendations(
        patient,
        fertilityIntent,
        cycles,
        5,
        canonicalExperience,
      ),
    [patient, fertilityIntent, cycles, canonicalExperience],
  );
  const visibleSections = useMemo(() => {
    const sections = prioritizeSectionsForGender(
      canonicalExperience?.viewer.sexAtBirth ?? "",
    );
    const focusedSectionKey = String(route.params?.focus ?? "");
    if (
      focusedSectionKey !== "semen-analysis" &&
      focusedSectionKey !== "genetic-screening"
    ) {
      return sections;
    }

    const focusedSectionIndex = sections.findIndex(
      (section) => section.key === focusedSectionKey,
    );
    if (focusedSectionIndex < 0) return sections;

    return [
      sections[focusedSectionIndex]!,
      ...sections.filter((_, index) => index !== focusedSectionIndex),
    ];
  }, [canonicalExperience?.viewer.sexAtBirth, route.params?.focus]);
  const tests = useMemo(
    () =>
      getBlocksByKind("test").filter(
        (test) => !test.body.isCommercialProductProfile,
      ),
    [],
  );

  const filteredTests = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tests;
    return tests.filter((test) => testMatchesSearch(test, q));
  }, [tests, search]);

  const timelineResults = useMemo(
    () =>
      TIMELINE_STAGES.map((stage) => ({
        stage,
        stageTests: filteredTests.filter(
          (test) => getTimelineStage(test) === stage.key,
        ),
      })).filter(({ stageTests }) => stageTests.length > 0),
    [filteredTests],
  );

  const filteredSections = useMemo(() => {
    const filter = TESTING_FILTERS.find((item) => item.key === activeFilter);
    if (!filter || filter.sectionKeys.length === 0) return visibleSections;
    return visibleSections.filter((section) =>
      filter.sectionKeys.includes(section.key),
    );
  }, [activeFilter, visibleSections]);

  useEffect(() => {
    setExpandedSectionKey((current) => {
      if (
        current &&
        filteredSections.some((section) => section.key === current)
      ) {
        return current;
      }
      return filteredSections[0]?.key ?? null;
    });
  }, [filteredSections]);

  // Group tests and products by section
  const testsBySection = useMemo(() => {
    const map: Record<string, ContentBlock<"test">[]> = {};
    for (const t of filteredTests) {
      const sec = t.body.section;
      if (sec) {
        (map[sec] ??= []).push(t);
      }
    }
    return map;
  }, [filteredTests]);

  const sectionResults = useMemo(
    () =>
      filteredSections
        .map((section) => ({
          section,
          sectionTests: testsBySection[section.key] ?? [],
        }))
        .filter(({ sectionTests }) => sectionTests.length > 0),
    [filteredSections, testsBySection],
  );

  const recommendedItems = useMemo<RecommendedItem[]>(() => {
    if (!isProfileComplete) return [];
    return recommendations
      .map((rec): RecommendedItem | null => {
        const test = tests.find((tt) => String(tt.id) === rec.testId);
        if (!test) return null;
        return {
          testId: rec.testId,
          title: test.title,
          reason: t(rec.reasonKey),
        };
      })
      .filter((x): x is RecommendedItem => x !== null);
  }, [isProfileComplete, recommendations, tests, t]);

  const onItemPress = useCallback(
    (item: ContentBlock<"test"> | ContentBlock<"product">) => {
      const target = getTestRouteTarget(item.id as string);
      if (target.screen === "TestProductDetail") {
        navigation.navigate("TestProductDetail", target.params);
        return;
      }
      navigation.navigate("TestDetail", {
        ...target.params,
        initialTab: "testing",
      });
    },
    [navigation],
  );

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <BackLink
          label={
            route.params?.returnTab ? t("you.shared.back") : t("access.back")
          }
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={
            {
              fontSize: 38,
              fontWeight: "700",
              color: colors.deepBrown,
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            } as Record<string, unknown>
          }
        >
          {t("access.testing.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 21,
          }}
          numberOfLines={2}
        >
          {t("access.testing.subtitle")}
        </Text>

        <MoonSearchInput
          placeholder={t("access.testing.search_placeholder")}
          value={search}
          onChangeText={setSearch}
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginTop: 18 }}
        />

        {isProfileComplete && recommendedItems.length > 0 ? (
          <TestingPlanHeroCard
            items={recommendedItems}
            onItemPress={(testId) => {
              const target = getTestRouteTarget(testId);
              if (target.screen === "TestProductDetail") {
                navigation.navigate("TestProductDetail", target.params);
              } else {
                navigation.navigate("TestDetail", {
                  ...target.params,
                  initialTab: "testing",
                });
              }
            }}
          />
        ) : !isProfileComplete ? (
          <TestingPlanHeroCard />
        ) : null}

        <View style={{ marginTop: 18, gap: 8 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: EMBER.colors.textFaint,
              letterSpacing: 1,
              textTransform: "uppercase",
            }}
          >
            {t("access.testing.view.label")}
          </Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 8, paddingRight: 24 }}
          >
            {TIMELINE_VIEW_MODES.map((mode) => {
              const isActive = mode.key === viewMode;
              return (
                <MoonFilterChip
                  key={mode.key}
                  label={t(mode.labelKey)}
                  selected={isActive}
                  onPress={() => setViewMode(mode.key)}
                  action="testing_view_mode"
                  tone="glass"
                  showCheck={false}
                />
              );
            })}
          </ScrollView>
        </View>

        {viewMode === "journey" ? (
          <View style={{ gap: 14, marginTop: 24 }}>
            {timelineResults.map(({ stage, stageTests }) => {
              const isExpanded = expandedTimelineKey === stage.key;
              return (
                <TimelineSubtaskCard
                  key={stage.key}
                  id={`access-testing-journey-${stage.key}`}
                  title={t(stage.titleKey)}
                  subtitle={t(stage.descriptionKey)}
                  titleImage={stage.illustration}
                  titleImageStyle={{ width: 38, height: 38 }}
                  expanded={isExpanded}
                  onToggle={() => {
                    capturePress(
                      pressLabel(t(stage.titleKey)),
                      "testing_journey_toggle",
                    );
                    setExpandedTimelineKey((current) =>
                      current === stage.key ? null : stage.key,
                    );
                  }}
                  defaultExpanded={false}
                >
                  <TimelineTestGroups
                    stage={stage.key}
                    tests={stageTests}
                    onPress={onItemPress}
                  />
                </TimelineSubtaskCard>
              );
            })}
            {timelineResults.length === 0 ? (
              search.trim() ? (
                <SearchEmptyState
                  title={t("access.testing.empty_search")}
                  onReset={() => {
                    setSearch("");
                    setActiveFilter("all");
                  }}
                />
              ) : (
                <EmptyState
                  density="compact"
                  title={t("access.testing.empty_no_options")}
                />
              )
            ) : null}
          </View>
        ) : (
          <>
            <View style={{ marginTop: 18, gap: 8 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "600",
                  color: EMBER.colors.textFaint,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                {t("access.testing.filters.label")}
              </Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 8, paddingRight: 24 }}
              >
                {TESTING_FILTERS.map((filter) => {
                  const isActive = filter.key === activeFilter;
                  return (
                    <MoonFilterChip
                      key={filter.key}
                      label={t(filter.labelKey)}
                      selected={isActive}
                      onPress={() => setActiveFilter(filter.key)}
                      action="testing_filter"
                      tone="glass"
                      showCheck={false}
                    />
                  );
                })}
              </ScrollView>
            </View>

            {/* Canonical test sections */}
            <View style={{ gap: 14, marginTop: 24 }}>
              {sectionResults.map(({ section, sectionTests }) => {
                const isExpanded = expandedSectionKey === section.key;
                const pgtParent =
                  section.key === "genetic-screening"
                    ? sectionTests.find(
                        (test) => String(test.id) === PGT_PARENT_ID,
                      )
                    : undefined;
                const visibleSectionTests = sectionTests.filter(
                  (test) =>
                    !PGT_SUBTYPE_IDS.has(String(test.id)) &&
                    (!pgtParent || String(test.id) !== PGT_PARENT_ID),
                );
                const orderedTests = pgtParent
                  ? [pgtParent, ...visibleSectionTests]
                  : sectionTests;

                return (
                  <TimelineSubtaskCard
                    key={section.key}
                    id={`access-testing-${section.key}`}
                    title={t(section.titleKey)}
                    subtitle={t(section.descriptionKey)}
                    titleImage={section.illustration}
                    titleImageStyle={{ width: 38, height: 38 }}
                    expanded={isExpanded}
                    onToggle={() => {
                      capturePress(
                        pressLabel(t(section.titleKey)),
                        "testing_section_toggle",
                      );
                      setExpandedSectionKey((current) =>
                        current === section.key ? null : section.key,
                      );
                    }}
                    defaultExpanded={false}
                  >
                    <View style={{ gap: 14 }}>
                      {section.key === "genetic-screening" && pgtParent ? (
                        <>
                          <SectionTitle>
                            {t("access.testing.subsections.embryo_testing")}
                          </SectionTitle>
                          <CanonicalTestGrid
                            tests={[pgtParent]}
                            onPress={onItemPress}
                          />
                          {visibleSectionTests.length > 0 ? (
                            <>
                              <SectionTitle>
                                {t(
                                  "access.testing.subsections.testing_for_people",
                                )}
                              </SectionTitle>
                              <CanonicalTestGrid
                                tests={visibleSectionTests}
                                onPress={onItemPress}
                              />
                            </>
                          ) : null}
                        </>
                      ) : (
                        <CanonicalTestGrid
                          tests={orderedTests}
                          onPress={onItemPress}
                        />
                      )}
                    </View>
                  </TimelineSubtaskCard>
                );
              })}
              {sectionResults.length === 0 ? (
                search.trim() || activeFilter !== "all" ? (
                  <SearchEmptyState
                    title={t("access.testing.empty_search")}
                    onReset={() => {
                      setSearch("");
                      setActiveFilter("all");
                    }}
                  />
                ) : (
                  <EmptyState
                    density="compact"
                    title={t("access.testing.empty_no_options")}
                  />
                )
              ) : null}
            </View>
          </>
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
